import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotify } from '@/hooks/useNotify';

interface ReactivationRequest {
  id: string;
  company_id: string;
  company_name: string;
  membership_id: string;
  status: 'pending' | 'approved' | 'rejected';
  request_message: string | null;
  admin_response_message: string | null;
  created_at: string;
  resolved_at: string | null;
}

interface DeactivatedMembershipWithRequests {
  id: string;
  company_id: string;
  company_name: string;
  role: string;
  deactivated_at: string;
  deactivation_reason: string;
  reactivation_request?: ReactivationRequest;
}

interface UseReactivationRequestsReturn {
  deactivatedMemberships: DeactivatedMembershipWithRequests[];
  isLoading: boolean;
  submitReactivationRequest: (membershipId: string, companyId: string, message?: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useReactivationRequests = (): UseReactivationRequestsReturn => {
  const { user } = useAuth();
  const notify = useNotify();
  const [deactivatedMemberships, setDeactivatedMemberships] = useState<DeactivatedMembershipWithRequests[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDeactivatedMemberships = async () => {
    if (!user) return;

    try {
      // Get deactivated memberships with companies
      const { data: memberships, error: membershipError } = await supabase
        .from('company_members')
        .select(`
          id,
          company_id,
          role,
          deactivated_at,
          deactivation_reason,
          companies!inner(name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'deactivated');

      if (membershipError) throw membershipError;

      // Get any VALID reactivation requests for these memberships (not stale)
      const membershipIds = memberships?.map(m => m.id) || [];
      const { data: requests, error: requestError } = await supabase
        .from('reactivation_requests')
        .select(`
          id,
          company_id,
          membership_id,
          status,
          request_message,
          admin_response_message,
          created_at,
          resolved_at,
          companies!inner(name)
        `)
        .in('membership_id', membershipIds)
        .eq('requested_by', user.id);

      if (requestError) throw requestError;

      // Combine memberships with their VALID requests (filter out stale requests)
      const membershipData = (memberships || []).map(membership => {
        // Find ALL requests for this membership and get the most recent valid one
        const membershipRequests = requests?.filter(r => r.membership_id === membership.id) || [];
        let validRequest = undefined;
        
        console.log(`[ReactivationRequests] Processing membership ${membership.id} with ${membershipRequests.length} requests`);
        
        if (membershipRequests.length > 0) {
          const memberDeactivatedAt = new Date(membership.deactivated_at);
          
          // Filter requests that were created AFTER the current deactivation
          const validRequests = membershipRequests.filter(request => {
            const requestCreatedAt = new Date(request.created_at);
            return requestCreatedAt > memberDeactivatedAt;
          });
          
          console.log(`[ReactivationRequests] Found ${validRequests.length} valid requests (created after deactivation)`);
          
          // Get the most recent valid request
          if (validRequests.length > 0) {
            const mostRecentRequest = validRequests.sort((a, b) => 
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
            
            console.log(`[ReactivationRequests] Most recent request: ${mostRecentRequest.id} (${mostRecentRequest.status})`);
            
            validRequest = {
              id: mostRecentRequest.id,
              company_id: mostRecentRequest.company_id,
              company_name: (mostRecentRequest.companies as any)?.name || 'Unknown Company',
              membership_id: mostRecentRequest.membership_id,
              status: mostRecentRequest.status as 'pending' | 'approved' | 'rejected',
              request_message: mostRecentRequest.request_message,
              admin_response_message: mostRecentRequest.admin_response_message,
              created_at: mostRecentRequest.created_at,
              resolved_at: mostRecentRequest.resolved_at,
            };
          }
        }
        
        return {
          id: membership.id,
          company_id: membership.company_id,
          company_name: (membership.companies as any)?.name || 'Unknown Company',
          role: membership.role,
          deactivated_at: membership.deactivated_at,
          deactivation_reason: membership.deactivation_reason,
          reactivation_request: validRequest
        };
      });

      setDeactivatedMemberships(membershipData);
    } catch (error) {
      console.error('Error fetching deactivated memberships:', error);
    }
  };

  const submitReactivationRequest = async (membershipId: string, companyId: string, message?: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.functions.invoke('request-reactivation', {
        body: { 
          membership_id: membershipId,
          company_id: companyId,
          message: message || ''
        }
      });

      if (error) throw error;

      notify.success('Your reactivation request has been sent to company administrators.');

      // Refresh data to show the new request
      await refetch();
    } catch (error: any) {
      console.error('Error submitting reactivation request:', error);
      notify.error(error.message || 'Failed to submit reactivation request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = async () => {
    setIsLoading(true);
    await fetchDeactivatedMemberships();
    setIsLoading(false);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [user]);

  return {
    deactivatedMemberships,
    isLoading,
    submitReactivationRequest,
    refetch
  };
};
